import { Progress } from "@volca/ui/components/ui/progress";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { DocumentIconFor } from "./document-icon";
import {
  UPLOAD_ITEM_STATE,
  type UploadItem,
  type UploadItemState,
} from "./types";

// Uploads start the moment files are chosen, so this is not a queue waiting on a button:
// it is the same list as the documents below it, one step earlier. The rows match, so the
// two read as one list where the top few are still arriving.
const stateLabel = (status: UploadItemState) => {
  switch (status.state) {
    case UPLOAD_ITEM_STATE.uploading:
      return `${status.progress}%`;
    case UPLOAD_ITEM_STATE.skipped:
      return "Skipped";
    case UPLOAD_ITEM_STATE.failed:
      return "Could not send";
    case UPLOAD_ITEM_STATE.done:
      return "Sent";
    default:
      return "Waiting";
  }
};

interface UploadProgressProps {
  items: UploadItem[];
}

export const UploadProgress = ({ items }: UploadProgressProps) => (
  <Stack className="w-full" gap={0} align="stretch" role="status">
    {items.map(({ file, status }, index) => {
      const failed = status.state === UPLOAD_ITEM_STATE.failed;

      return (
        <Stack
          key={`${file.name}-${index}`}
          gap={1}
          align="stretch"
          className="w-full border-b border-line py-3 last:border-b-0"
        >
          <Stack className="w-full" direction="row" gap={2} align="center">
            <DocumentIconFor
              fileName={file.name}
              className="h-5 w-5 shrink-0 text-subtle"
            />
            <Text className="min-w-0 flex-1 truncate" title={file.name}>
              {file.name}
            </Text>
            <Text
              variant="caption"
              className={cn({
                "text-danger": failed,
                "text-subtle": !failed,
              })}
            >
              {stateLabel(status)}
            </Text>
          </Stack>
          {status.state === UPLOAD_ITEM_STATE.uploading && (
            <Progress value={status.progress} />
          )}
          {failed && (
            <Text variant="caption" className="text-danger">
              {status.message}
            </Text>
          )}
        </Stack>
      );
    })}
  </Stack>
);
